/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @angular-eslint/no-host-metadata-property */
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { DOWN_ARROW, ENTER, ESCAPE, TAB, UP_ARROW } from '@angular/cdk/keycodes';
import {
   ComponentType,
   ConnectedPosition,
   FlexibleConnectedPositionStrategy,
   Overlay,
   OverlayConfig,
   OverlayRef,
   PositionStrategy,
   ScrollStrategy,
   ViewportRuler,
} from '@angular/cdk/overlay';
import {
   AfterViewInit,
   ChangeDetectorRef,
   ComponentRef,
   Directive,
   DoCheck,
   ElementRef,
   EventEmitter,
   FactoryProvider,
   HostListener,
   Inject,
   InjectionToken,
   Input,
   NgZone,
   OnDestroy,
   OnInit,
   Optional,
   Output,
   Renderer2,
   Self,
   ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm, UntypedFormControl } from '@angular/forms';
import { ErrorStateMatcher, _getOptionScrollPosition } from '@angular/material/core';
import { MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { EMPTY, Observable, Subject, Subscription, defer, fromEvent, merge } from 'rxjs';
import { delay, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { LcsOptionComponent, LcsOptionSelectionChange } from '../select/option/option.component';
import { LcsSingleSelect } from './single-select-base';

export const SINGLE_SELECT_OPTION_HEIGHT = 48;

export const SINGLE_SELECT_MAX_PANEL_HEIGHT = 256;

export const LCS_SINGLE_SELECT_SCROLL_STRATEGY_FACTORY = new InjectionToken<() => ScrollStrategy>(
   'lcs-single-select-scroll-strategy-factory'
);

export function lcsSingleSelectDefaultScrollStrategyFactory(overlay: Overlay): () => ScrollStrategy {
   return () => {
      return overlay.scrollStrategies.reposition();
   };
}

export const LCS_SINGLE_SELECT_SCROLL_STRATEGY_FACTORY_PROVIDER: FactoryProvider = {
   provide: LCS_SINGLE_SELECT_SCROLL_STRATEGY_FACTORY,
   deps: [Overlay],
   useFactory: lcsSingleSelectDefaultScrollStrategyFactory,
};

let nextUniqueId = 0;

@Directive({
   exportAs: 'lcsSingleSelect',
   // eslint-disable-next-line @angular-eslint/no-host-metadata-property
   host: {
      '[attr.aria-activedescendant]': 'panelOpen ? activeOption?.id : null',
      '[attr.aria-autocomplete]': '"list"',
      '[attr.aria-describedby]': 'ariaDescribedby',
      '[attr.aria-expanded]': 'panelOpen.toString()',
      '[attr.aria-haspopup]': 'true',
      '[attr.aria-invalid]': 'errorState',
      '[attr.aria-owns]': '!panelOpen ? null : singleSelect?.id',
      '[attr.aria-required]': 'required.toString()',
      '[attr.autocomplete]': 'autocompleteAttribute',
      '[attr.id]': 'id',
      '[attr.placeholder]': 'placeholder',
      '[class.lcs-single-select-disabled]': 'disabled',
      '[class.lcs-single-select-invalid]': 'errorState',
      '[class.mat-input-element]': 'true',
      '[disabled]': 'disabled',
      '[required]': 'required',
      // eslint-disable-next-line quote-props
      'role': 'combobox',
      // eslint-disable-next-line sort-keys
      '(blur)': 'onBlur()',
      '(focus)': 'onFocus()',
      '(input)': 'onInput($event)',
      '(keydown)': 'onKeydown($event)',
   },
   providers: [{ provide: MatFormFieldControl, useExisting: LcsSingleSelectDirective }, MatTooltip],
   // eslint-disable-next-line @angular-eslint/directive-selector
   selector: 'input[lcsSingleSelect]',
   standalone: false,
})
export class LcsSingleSelectDirective<TItem>
   implements AfterViewInit, ControlValueAccessor, DoCheck, MatFormFieldControl<TItem>, OnDestroy, OnInit
{
   // eslint-disable-next-line @angular-eslint/no-input-rename
   @Input('lcsSingleSelect')
   set singleSelect(singleSelect: LcsSingleSelect<TItem>) {
      this._singleSelect = singleSelect;
      this.detachOverlay();
      this.optionChangeSubscription?.unsubscribe();
      if (this.singleSelect && this.singleSelect.displayWith) {
         this.optionChangeSubscription = this.singleSelect.optionsChange?.subscribe((s) => {
            this.setInputValue(this.singleSelect.displayWith(this.value));
         });
      }
   }

   get singleSelect(): LcsSingleSelect<TItem> {
      return this._singleSelect;
   }

   private _singleSelect: LcsSingleSelect<TItem>;

   private optionChangeSubscription: Subscription | null = null;

   @Input('lcsSingleSelectValue')
   get value(): TItem | null {
      return this._value;
   }

   set value(value: TItem | null) {
      this.handleUpdatedValue(value);
   }

   private _value: TItem | null;

   @Input()
   set id(id: string) {
      this.generateId(id);
   }

   get id(): string {
      return this._id;
   }

   private _id: string;

   @Input()
   get required(): boolean {
      return this._required;
   }

   set required(value: boolean) {
      this._required = coerceBooleanProperty(value);
   }

   private _required = false;

   @Input()
   get disabled(): boolean {
      if (this.ngControl && this.ngControl.disabled != null) {
         return this.ngControl.disabled;
      }
      return this._disabled;
   }

   set disabled(value: boolean) {
      this._disabled = coerceBooleanProperty(value);
   }

   private _disabled = false;

   @Input()
   get allowManualInput(): boolean {
      return this._allowManualInput;
   }

   set allowManualInput(value: boolean) {
      this._allowManualInput = coerceBooleanProperty(value);
   }

   private _allowManualInput = false;

   @Input() singleItemAutoSelect: boolean = true;

   // eslint-disable-next-line @angular-eslint/no-input-rename
   @Input('autocomplete') autocompleteAttribute = 'off';

   @Input() errorStateMatcher: ErrorStateMatcher;

   @Input() placeholder: string;

   @Input() openPanelOnFocus: boolean = false;

   @Input() panelClass: string;

   @Input() valueBadgeIcon: ComponentType<unknown> | null = null;

   @Input() showValueBadgeIcon: ((value: TItem | null) => boolean) | null = null;

   // eslint-disable-next-line @angular-eslint/no-output-rename
   @Output('lcsSingleSelectValueChange') valueChange = new EventEmitter<TItem | null>();

   // eslint-disable-next-line @angular-eslint/no-output-rename
   @Output('lcsSingleSelectRemoveValueChange') valueRemove = new EventEmitter<TItem | null>();

   @Output() overlayAttached = new EventEmitter<boolean>();

   autofilled = false;

   controlType = 'lcs-single-select';

   empty = false;

   errorState = false;

   focused = false;

   shouldLabelFloat = true;

   stateChanges = new Subject<void>();

   ariaDescribedby: string | null;

   existingItems: TItem[] | null;

   removedItem: TItem | null;

   valueBadgeIconComponentRef: ComponentRef<unknown> | null = null;

   get activeOption(): LcsOptionComponent<TItem> | null {
      if (this.singleSelect && this.singleSelect.keyManager) {
         return this.singleSelect.keyManager.activeItem;
      }
      return null;
   }

   /**
    * A stream of actions that should close the autocomplete panel, including
    * when an option is selected, on blur, and when TAB is pressed.
    */
   get panelClosingActions(): Observable<LcsOptionSelectionChange | null> {
      return merge(
         this.optionSelections,
         this.closeKeyEventStream,
         this.singleSelect.keyManager.tabOut.pipe(
            filter(() => {
               return !this._overlayAttached;
            }),
            tap(() => {
               if (this.activeOption && this.userActed) {
                  this._value = this.activeOption.value;
                  this.emitValue(this._value);
                  this.singleSelect.emitSelectEvent(this.activeOption);
               }
            })
         ),
         this.getOutsideClickStream(),
         this.overlayRef
            ? this.overlayRef.detachments().pipe(
                 filter(() => {
                    return this._overlayAttached;
                 })
              )
            : EMPTY
      ).pipe(
         // Normalize the output so we return a consistent type.
         map((event) => {
            return event instanceof LcsOptionSelectionChange ? event : null;
         })
      );
   }

   get panelOpen(): boolean {
      return this._overlayAttached && this.singleSelect.isPanelVisible;
   }

   /** Stream of single-select option selections. */
   readonly optionSelections: Observable<LcsOptionSelectionChange> = defer(() => {
      if (this.singleSelect && this.singleSelect.options) {
         return merge(
            ...this.singleSelect.options.map((option) => {
               return option.selectionChange;
            })
         );
      }

      // If there are any subscribers before `ngAfterViewInit`, the `autocomplete` will be undefined.
      // Return a stream that we'll replace with the real one once everything is in place.
      return this.ngZone.onStable.asObservable().pipe(
         take(1),
         switchMap(() => {
            return this.optionSelections;
         })
      );
   }) as Observable<LcsOptionSelectionChange>;

   private closingActionsSubscription = Subscription.EMPTY;

   private onChange?: (value: TItem | null) => void;

   private onTouched?: () => void;

   private _overlayAttached = false;

   private overlayRef: OverlayRef | null;

   private positionStrategy: FlexibleConnectedPositionStrategy;

   private scrollStrategyFactory: () => ScrollStrategy;

   /** Stream of keyboard events that can close the panel. */
   private readonly closeKeyEventStream = new Subject<void>();

   private isDestroyed = false;

   private readonly destroyed = new Subject<void>();

   private previousInputValue: string | number | null;

   private arrowElement?: HTMLSpanElement;

   private arrowSelectAreaElement?: HTMLSpanElement;

   private hasPendingDisplayValue = false;

   private userActed = false;

   private singleSelectOptionsChangesSubscription: Subscription;

   private initialValueLoaded = false;

   private initialIncludeEmptyOption: boolean;

   private options: string[] = [];

   private maxTextWidth: number = 0;

   constructor(
      @Self() @Optional() public ngControl: NgControl | null,
      private elementRef: ElementRef<HTMLInputElement>,
      @Optional() private parentFormGroup: FormGroupDirective,
      @Optional() private parentForm: NgForm,
      private defaultErrorStateMatcher: ErrorStateMatcher,
      private renderer: Renderer2,
      private ngZone: NgZone,
      private overlay: Overlay,
      @Optional() private formField: MatFormField,
      private viewContainerRef: ViewContainerRef,
      @Inject(LCS_SINGLE_SELECT_SCROLL_STRATEGY_FACTORY) scrollStrategyFactory: () => ScrollStrategy,
      private viewportRuler: ViewportRuler,
      private changeDetectorRef: ChangeDetectorRef,
      private tooltip: MatTooltip
   ) {
      if (ngControl) {
         ngControl.valueAccessor = this;
      }

      this.scrollStrategyFactory = scrollStrategyFactory;
   }

   ngAfterViewInit(): void {
      const arrow: HTMLSpanElement = this.renderer.createElement('span');
      arrow.classList.add('lcs-single-select-arrow');
      this.elementRef.nativeElement.insertAdjacentElement('afterend', arrow);
      const arrowSelectArea: HTMLSpanElement = this.renderer.createElement('span');
      arrowSelectArea.classList.add('lcs-single-select-arrow-select-area');
      arrow.insertAdjacentElement('afterbegin', arrowSelectArea);

      this.arrowSelectAreaElement = arrowSelectArea;
      this.arrowElement = arrow;

      this.arrowSelectAreaElement.onclick = (event) => {
         if (this.singleSelect.isOpen) {
            this.close();
            event.stopPropagation();
         }
      };

      this.setInputValue(this.singleSelect.displayWith(this._value));
      this.handleValueBadge(this._value);

      this.initialIncludeEmptyOption = this.singleSelect.isIncludeEmptyOption;

      // Listen for changes to the selects options
      this.singleSelectOptionsChangesSubscription = this.singleSelect.options.changes.subscribe((newChanges) => {
         this.options = newChanges._results.map((results) => this.getValueString(results.value));
         if (this.options.length > 0) {
            this.overlayRef?.updateSize({ width: this.getOriginWidth() });
            const value: any = this.value;
            const index: number = this.options.findIndex(
               (name) => name?.toLocaleLowerCase() === this.getValueString(value)?.toLocaleLowerCase()
            );

            setTimeout(() => {
               if (index == -1 && (this.singleSelect.autoActiveFirstOption || this.userActed)) {
                  this.singleSelect.keyManager.setFirstItemActive();
                  this.singleSelect.setScrollTop(0);
               } else {
                  this.singleSelect.keyManager.setActiveItem(index);
                  this.scrollToOption();
               }
            }, 50);
         }

         // If there is only one option and its required, set that option by default
         if (
            newChanges._results.length === 1 &&
            this._required &&
            !this.initialValueLoaded &&
            this.singleItemAutoSelect
         ) {
            this.handleSingleRequiredField(newChanges._results[0].value);
         }
         this.initialValueLoaded = true;
      });

      // Trigger an open() to load in initial options
      requestAnimationFrame(() => {
         this.open();
      });
      // Trigger a close() after initial options are loaded in and default selection for
      // a single option on a required field is made
      requestAnimationFrame(() => {
         this.close();
      });
   }

   ngDoCheck(): void {
      if (this.ngControl) {
         // We need to re-evaluate this on every change detection cycle, because there are some
         // error triggers that we can't subscribe to (e.g. parent form submissions). This means
         // that whatever logic is in here has to be super lean or we risk destroying the performance.
         this.updateErrorState();
      }
   }

   ngOnInit(): void {
      if (!this.singleSelect) {
         throw new Error('The `lcsSingleSelect` input is required for the lcsSingleSelect directive.');
      }

      // force id setter in order to generate an id if one doesn't exist
      if (!this.id) {
         this.generateId();
      }
   }

   ngOnDestroy(): void {
      this.destroyPanel();
      this.destroyed.next();
      this.isDestroyed = true;
      this.closeKeyEventStream.complete();
      if (this.arrowElement) {
         this.arrowElement.remove();
      }
      if (this.arrowSelectAreaElement) {
         this.arrowSelectAreaElement.remove();
      }
      this.singleSelectOptionsChangesSubscription?.unsubscribe();
   }

   @HostListener('mouseover') mouseOver() {
      this.tooltip.position = 'below';
      this.tooltip.showDelay = 500;
      this.tooltip.tooltipClass = 'single-select-tooltip';
      this.tooltip.message = this.getValueString(this.value) || '';
      this.tooltip.show();
   }

   @HostListener('mouseleave') mouseLeave() {
      this.tooltip.hide();
   }

   handleSingleRequiredField(option: TItem | null): void {
      this._value = option;
      this.setValueAndClose(null);
      this.emitValue(option);
   }

   updateExistingItems(value: TItem[] | null): void {
      this.existingItems = value;
   }

   removedExitsingItem(value: TItem | null): void {
      this.removedItem = value;
      this.valueRemove.emit(value);
   }

   open(): void {
      this.attachOverlay();
   }

   close(): void {
      if (!this._overlayAttached) {
         return;
      }

      if (this.panelOpen) {
         this.singleSelect.closed.emit();
      }

      this.singleSelect.isOpen = false;
      this.detachOverlay();

      // Note that in some cases this can end up being called after the component is destroyed.
      // Add a check to ensure that we don't try to run change detection on a destroyed view.
      if (!this.isDestroyed) {
         // We need to trigger change detection manually, because
         // `fromEvent` doesn't seem to do it at the proper time.
         // This ensures that the label is reset when the
         // user clicks outside.
         // Also, the overlay panel doesn't get removed if the ngZone
         // is already stable when we close the overlay.
         this.changeDetectorRef.detectChanges();
      }
   }

   setDescribedByIds(ids: string[]): void {
      this.ariaDescribedby = ids.length > 0 ? ids.join(' ') : null;
   }

   onContainerClick(): void {
      if (!this.focused) {
         this.focus();
      }
      if (!this._overlayAttached) {
         this.attachOverlay();
      }
   }

   focus(): void {
      this.elementRef.nativeElement.focus();
      this.elementRef.nativeElement.select();
   }

   onBlur(): void {
      if (this.focused) {
         this.focused = false;
         if (this.onTouched) {
            this.onTouched();
         }
         this.stateChanges.next();
         this.userActed = false;
      }
   }

   onFocus(): void {
      this.focused = true;
      this.elementRef.nativeElement.select();
      this.previousInputValue = this.elementRef.nativeElement.value;
      this.stateChanges.next();
      if (this.openPanelOnFocus) {
         this.open();
      }
   }

   onInput(event: KeyboardEvent): void {
      const target = event.target as HTMLInputElement;
      let value: number | string | null = target.value;
      if (target.type === 'number') {
         value = value === '' ? null : parseFloat(value);
      }

      // If the input has a placeholder, IE will fire the `input` event on page load,
      // focus and blur, in addition to when the user actually changed the value. To
      // filter out all of the extra events, we save the value on focus and between
      // `input` events, and we check whether it changed.
      // See: https://connect.microsoft.com/IE/feedback/details/885747/
      if (this.previousInputValue !== value) {
         this.previousInputValue = value;

         if (!this.disabled && document.activeElement === event.target) {
            this.open();
         }
      }

      if (value == null || (typeof value === 'string' && !value && !value.trim())) {
         if (this._value !== null) {
            this.emitValue(null);
            this._value = null;
         }
         this.setInputValue('');
      }

      if (value == null || value == '') {
         this.singleSelect.isIncludeEmptyOption = this.initialIncludeEmptyOption;
         this.singleSelect.keyManager.setFirstItemActive();
      } else {
         this.singleSelect.isIncludeEmptyOption = false;
         this.singleSelect.keyManager.setFirstItemActive();
      }
   }

   onKeydown(event: KeyboardEvent): void {
      const keyCode = event.keyCode;

      // Prevent the default action on all escape key presses. This is here primarily to bring IE
      // in line with other browsers. By default, pressing escape on IE will cause it to revert
      // the input value to the one that it had on focus, however it won't dispatch any events
      // which means that the model value will be out of sync with the view.
      if (keyCode === ESCAPE) {
         event.preventDefault();
      }

      if (keyCode !== TAB) {
         this.userActed = true;
      }

      const activeOption = this.activeOption;

      if (activeOption && (keyCode === ENTER || keyCode === TAB) && this.panelOpen) {
         activeOption.selectViaInteraction();
         event.preventDefault();
      } else if (keyCode === TAB && !activeOption) {
         this.close();
      } else {
         const prevActiveItem = activeOption;
         const isArrowKey = keyCode === UP_ARROW || keyCode === DOWN_ARROW;

         if (this.panelOpen || keyCode === TAB) {
            this.singleSelect.keyManager.onKeydown(event);
         } else if (isArrowKey && !this.disabled) {
            this.open();
         }

         if (isArrowKey || activeOption !== prevActiveItem) {
            this.scrollToOption();
         }
      }
   }

   writeValue(value: TItem | null): void {
      this.value = value;
   }

   registerOnChange(fn: (value: TItem | null) => void): void {
      this.onChange = fn;
   }

   registerOnTouched(fn: () => void): void {
      this.onTouched = fn;
   }

   setDisabledState(disabled: boolean): void {
      this.disabled = disabled;
   }

   private updateErrorState() {
      const oldState = this.errorState;
      const parent = this.parentFormGroup || this.parentForm;
      const matcher = this.errorStateMatcher || this.defaultErrorStateMatcher;
      const control = this.ngControl ? (this.ngControl.control as UntypedFormControl) : null;
      const newState = matcher.isErrorState(control, parent);

      if (newState !== oldState) {
         this.errorState = newState;
         this.stateChanges.next();
      }
   }

   private emitValue(value: TItem | null): void {
      if (this.onChange) {
         this.onChange(value);
      }
      this.valueChange.emit(value);
      this.handleValueBadge(value);
   }

   private setInputValue(value: string | null): void {
      this.previousInputValue = value;
      const normalizedValue = value == null ? '' : value;
      this.renderer.setProperty(this.elementRef.nativeElement, 'value', normalizedValue);
   }

   private attachOverlay(): void {
      if (!this.singleSelect) {
         throw new Error('Missing autocomplete');
      }

      if (this.disabled) return;

      let overlayRef: OverlayRef | null = this.overlayRef;

      if (!overlayRef) {
         overlayRef = this.overlay.create(this.getOverlayConfig());
         this.overlayRef = overlayRef;

         overlayRef.keydownEvents().subscribe((event) => {
            // Close when pressing ESCAPE or ALT + UP_ARROW, based on the a11y guidelines.
            // See: https://www.w3.org/TR/wai-aria-practices-1.1/#textbox-keyboard-interaction
            if (event.keyCode === ESCAPE || (event.keyCode === UP_ARROW && event.altKey)) {
               this.closeKeyEventStream.next();

               // We need to stop propagation, otherwise the event will eventually
               // reach the input itself and cause the overlay to be reopened.
               event.stopPropagation();
               event.preventDefault();
            }
         });

         this.viewportRuler
            .change()
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => {
               if (this.panelOpen && overlayRef) {
                  overlayRef.updateSize({ width: this.getOriginWidth() });
               }
            });
      } else {
         // update just in case the origin or size has changed since we last opened
         this.positionStrategy.setOrigin(this.getConnectedElement());
         overlayRef.updateSize({ width: this.getOriginWidth() });
      }

      if (overlayRef && !overlayRef.hasAttached()) {
         overlayRef.attach(this.singleSelect.portal);
         setTimeout(() => {
            this.scrollToOption();
         });
         this.overlayAttached.emit(true);
         this.closingActionsSubscription = this.subscribeToClosingActions();
      }

      const wasOpen = this.panelOpen;

      this.singleSelect.setVisibility();
      this.singleSelect.isOpen = this._overlayAttached = true;

      if (this.panelOpen && wasOpen !== this.panelOpen) {
         this.singleSelect.opened.emit();
      }
   }

   /**
    * Listens to a stream of panel closing actions and resets the
    * stream every time the option list changes.
    */
   private subscribeToClosingActions(): Subscription {
      if (this.isDestroyed) {
         return Subscription.EMPTY;
      }

      const firstStable = this.ngZone.onStable.asObservable().pipe(take(1));
      const optionChanges = this.singleSelect.options.changes.pipe(
         tap(() => {
            return this.positionStrategy.reapplyLastPosition();
         }),
         // Defer emitting to the stream until the next tick, because changing
         // bindings in here will cause "changed after checked" errors.
         delay(0)
      );
      // When the zone is stable initially, and when the option list changes...
      return (
         merge(firstStable, optionChanges)
            .pipe(
               // create a new stream of panelClosingActions, replacing any previous streams
               // that were created, and flatten it so our stream only emits closing events...
               switchMap(() => {
                  const wasOpen = this.panelOpen;
                  this.singleSelect.setVisibility();

                  if (this.panelOpen && this.overlayRef) {
                     this.overlayRef.updatePosition();

                     // If the `panelOpen` state changed, we need to make sure to emit the `opened`
                     // event, because we may not have emitted it when the panel was attached. This
                     // can happen if the users opens the panel and there are no options, but the
                     // options come in slightly later or as a result of the value changing.
                     if (wasOpen !== this.panelOpen) {
                        this.singleSelect.opened.emit();
                     }
                  }

                  return this.panelClosingActions;
               }),
               // when the first closing event occurs...
               take(1)
            )
            // set the value, close the panel, and complete.
            .subscribe((event) => {
               return this.setValueAndClose(event);
            })
      );
   }

   private getOverlayConfig(): OverlayConfig {
      return new OverlayConfig({
         direction: 'ltr',
         positionStrategy: this.getPositionStrategy(),
         scrollStrategy: this.scrollStrategyFactory(),
         width: this.getOriginWidth(),
         panelClass: this.panelClass ?? undefined,
      });
   }

   private getPositionStrategy(): PositionStrategy {
      const strategy = this.overlay
         .position()
         .flexibleConnectedTo(this.getConnectedElement())
         .withFlexibleDimensions(false)
         .withPush(false);

      this.setStrategyPositions(strategy);
      this.positionStrategy = strategy;
      return strategy;
   }

   /** Sets the positions on a position strategy based on the directive's input state. */
   private setStrategyPositions(positionStrategy: FlexibleConnectedPositionStrategy) {
      const belowPosition: ConnectedPosition = {
         originX: 'start',
         originY: 'bottom',
         overlayX: 'start',
         overlayY: 'top',
      };
      const abovePosition: ConnectedPosition = {
         originX: 'start',
         originY: 'top',
         overlayX: 'start',
         overlayY: 'bottom',

         // The overlay edge connected to the trigger should have squared corners, while
         // the opposite end has rounded corners. We apply a CSS class to swap the
         // border-radius based on the overlay position.
         panelClass: 'lcs-single-select-panel-above',
      };

      positionStrategy.withPositions([belowPosition, abovePosition]);
   }

   private getConnectedElement() {
      return this.formField ? this.formField.getConnectedOverlayOrigin() : this.elementRef;
   }

   private getOriginWidth(): number {
      const boundingWidth = this.getConnectedElement().nativeElement.getBoundingClientRect().width;
      this.maxTextWidth = this.getMaxTextWidth(this.options);
      return boundingWidth > this.maxTextWidth ? boundingWidth : this.maxTextWidth;
   }

   private getMaxTextWidth(texts: string[]): number {
      const lengths: number[] = [];
      let maxWidth: number = 0;
      for (let i = 0; i < this.options?.length; i++) {
         const width: number | null = this.getTextWidth(this.options[i]);
         maxWidth = width ? (width > maxWidth ? width : maxWidth) : maxWidth;
      }

      return maxWidth > this.maxTextWidth ? maxWidth : this.maxTextWidth;
   }

   /**
    * Gets the width of the given text using the canvas.measureText method. The measurement
    * takes into consideration the font and size of the text. This function also adds a
    * standard padding width to the calculated width of the text to account for padding in
    * the component. The canvas element is removed after calculation is complete.
    * @param text The text to measure.
    * @returns The length of the text in pixels.
    */
   private getTextWidth(text: string): number | null {
      const computedStyle = getComputedStyle(this.elementRef.nativeElement);
      const font = computedStyle.font;
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const padding = 36; // pixels
      if (context) {
         context.font = font;
      }

      canvas.remove();
      return context ? context.measureText(text).width + padding : null;
   }

   /** Stream of clicks outside of the autocomplete panel. */
   // eslint-disable-next-line @typescript-eslint/no-explicit-any
   private getOutsideClickStream(): Observable<any> {
      const outsideClicks =
         this.overlayRef?.outsidePointerEvents().pipe(
            filter((event) => {
               const clickTarget = event.target as HTMLElement;
               const formField = this.formField ? this.formField._elementRef.nativeElement : null;

               return clickTarget !== this.elementRef.nativeElement && (!formField || !formField.contains(clickTarget));
            })
         ) ?? EMPTY;

      const iframeClicks = fromEvent<FocusEvent>(window, 'blur').pipe(
         filter((event) => {
            return (
               !!event.target && !!(event.target as Element).tagName && (event.target as Element).tagName === 'IFRAME'
            );
         })
      );

      return merge(iframeClicks, outsideClicks);
   }

   /**
    * This method closes the panel, and if a value is specified, also sets the associated
    * control to that value. It will also mark the control as dirty.
    */
   private setValueAndClose(event: LcsOptionSelectionChange<TItem> | null): void {
      if (event && event.source) {
         this.clearPreviousSelectedOption(event.source);

         let displayValue: string | null;
         if (this.singleSelect.displayWith) {
            displayValue = this.singleSelect.displayWith(event.source.value);
         } else {
            // if there's no displayWith, assume the value is a string
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            displayValue = event.source.value as any as string;
         }

         if (this._value !== event.source.value) {
            this._value = event.source.value;
            this.setInputValue(displayValue);
            this.emitValue(event.source.value);
            this.singleSelect.emitSelectEvent(event.source);
         } else if (this.elementRef.nativeElement.value !== displayValue) {
            this.setInputValue(displayValue);
         }

         this.elementRef.nativeElement.focus();
      } else if (this._value) {
         let displayValue: string | null;
         if (this.singleSelect.displayWith) {
            displayValue = this.singleSelect.displayWith(this._value);
         } else {
            // if there's no displayWith, assume the value is a string
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            displayValue = this._value as any as string;
         }

         if (this.elementRef.nativeElement.value !== displayValue) {
            this.setInputValue(displayValue);
         }
      } else {
         this.setInputValue('');
      }

      this.close();
   }

   private clearPreviousSelectedOption(skip: LcsOptionComponent<TItem>) {
      this.singleSelect.options.forEach((option) => {
         if (option !== skip && option.selected) {
            option.deselect();
         }
      });
   }

   private destroyPanel(): void {
      if (this.overlayRef) {
         this.close();
         this.overlayRef.dispose();
         this.overlayRef = null;
      }
   }

   /**
    * Given that we are not actually focusing active options, we must manually adjust scroll
    * to reveal options below the fold. First, we find the offset of the option from the top
    * of the panel. If that offset is below the fold, the new scrollTop will be the offset -
    * the panel height + the option height, so the active option will be just visible at the
    * bottom of the panel. If that offset is above the top of the visible panel, the new scrollTop
    * will become the offset. If that offset is visible within the panel already, the scrollTop is
    * not adjusted.
    */
   private scrollToOption(): void {
      const index = this.singleSelect.keyManager.activeItemIndex || 0;

      if (index === 0) {
         // If we've got one group label before the option and we're at the top option,
         // scroll the list to the top. This is better UX than scrolling the list to the
         // top of the option, because it allows the user to read the top group's label.
         this.singleSelect.setScrollTop(0);
      } else {
         const newScrollPosition = _getOptionScrollPosition(
            index * SINGLE_SELECT_OPTION_HEIGHT,
            SINGLE_SELECT_OPTION_HEIGHT,
            this.singleSelect.getScrollTop(),
            SINGLE_SELECT_MAX_PANEL_HEIGHT
         );

         this.singleSelect.setScrollTop(newScrollPosition);
      }
   }

   private handleUpdatedValue(value: TItem | null): void {
      if (value !== this._value || this.hasPendingDisplayValue) {
         this._value = value;
         this.stateChanges.next();
         if (this.singleSelect && this.singleSelect.displayWith) {
            this.setInputValue(this.singleSelect.displayWith(value));
            this.hasPendingDisplayValue = false;
         } else {
            this.hasPendingDisplayValue = true;
         }
      }
   }

   private handleValueBadge(value: TItem | null) {
      if (this.valueBadgeIcon && this.showValueBadgeIcon && this.showValueBadgeIcon(value)) {
         this.valueBadgeIconComponentRef = this.viewContainerRef.createComponent(this.valueBadgeIcon);
      } else if (this.valueBadgeIconComponentRef) {
         this.valueBadgeIconComponentRef.destroy();
      }
   }

   private generateId(id?: string | null): void {
      this._id = id || `lcs-single-select-${nextUniqueId++}`;
   }

   private detachOverlay(): void {
      this._overlayAttached = false;
      this.overlayAttached.emit(false);
      this.closingActionsSubscription.unsubscribe();
      if (this.overlayRef) {
         this.overlayRef.detach();
      }
   }

   private getValueString(value: any): string | null {
      if (this.singleSelect && this.singleSelect.displayWith) {
         return this.singleSelect.displayWith(value);
      } else {
         return null;
      }
   }

   /* eslint-disable @typescript-eslint/member-ordering */
   /* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */
   static ngAcceptInputType_autoActiveFirstOption: BooleanInput;

   static ngAcceptInputType_disabled: BooleanInput;
}
